import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileBarChart, History, Timer, ShieldCheck, Download, Loader2, FileSpreadsheet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OmCard, OmHead, Pill, GOLD, MUTED, NAVY, FIELD } from "@/components/om/OmBits";

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const REPORTS = [
  ["monthly", FileBarChart, "Monthly O&M Summary", "PM compliance, CM summary, asset health and inventory consumption for the period."],
  ["equipment", History, "Equipment History", "Full maintenance history for a single asset — every PM and CM record, fault trend and spares consumed."],
  ["sla", Timer, "SLA Performance", "CM ticket SLA compliance by severity and by technician, with every breach listed."],
  ["compliance", ShieldCheck, "Regulatory Compliance", "Statutory checks completed versus due, with the digital sign-off record for each."],
];

export default function OmReports() {
  const [d, setD] = useState(null);
  const [assets, setAssets] = useState([]);
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [assetId, setAssetId] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/om/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/om/assets").then(({ data }) => { setAssets(data); if (data[0]) setAssetId(data[0].id); }).catch(() => {});
  }, []);

  const download = async (kind, fmt) => {
    if (kind === "equipment" && !assetId) return toast.error("Pick an asset for the equipment history report");
    setBusy(`${kind}-${fmt}`);
    try {
      const res = await api.get(`/om/reports/${kind}`, {
        params: { fmt, start, end, asset_id: kind === "equipment" ? assetId : "" },
        responseType: "blob",
      });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1]
        || `plan-sarthi-${kind}.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${name} downloaded`);
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { detail = null; }
      }
      toast.error(apiError(detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="om-reports-page">
      <OmHead title="Reports & Compliance" subtitle="Branded, board-ready PDF and Excel packs generated on the server." />

      <OmCard className="mb-3" testid="om-reports-controls">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={FIELD} data-testid="om-report-start" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={FIELD} data-testid="om-report-end" />
          </div>
          <div className="lg:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Asset (equipment history only)</Label>
            <select value={assetId} onChange={(e) => setAssetId(e.target.value)} className="w-full h-9 px-2 text-[13px] border"
              style={{ ...FIELD, borderRadius: 6 }} data-testid="om-report-asset-select">
              <option value="">Select an asset</option>
              {assets.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
          </div>
        </div>
      </OmCard>

      {d ? (
        <OmCard className="mb-3" testid="om-reports-summary">
          <h3 className="text-white font-semibold text-[15px] mb-3">This month at a glance</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 text-[12.5px]">
            {[["PM compliance", `${d.kpi.pm_compliance}%`], ["Overdue PM", d.kpi.overdue_pm],
              ["Open tickets", d.kpi.open_critical + d.kpi.open_high + d.kpi.open_medium],
              ["SLA compliance", `${d.kpi.sla_compliance}%`], ["Avg asset health", d.kpi.avg_health]].map(([l, v]) => (
              <div key={l}>
                <div className="text-[10.5px] uppercase tracking-[0.12em]" style={{ color: MUTED }}>{l}</div>
                <div className="font-semibold tabular-nums mt-0.5" style={{ color: GOLD, fontSize: 20 }}>{v}</div>
              </div>
            ))}
          </div>
        </OmCard>
      ) : null}

      <div className="grid md:grid-cols-2 gap-3">
        {REPORTS.map(([kind, Icon, title, desc]) => (
          <OmCard key={kind} testid={`om-report-${kind}`}>
            <div className="flex items-start gap-3">
              <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: GOLD }} />
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-[15px]">{title}</h3>
                <p className="text-[12.5px] mt-1 leading-relaxed" style={{ color: MUTED }}>{desc}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Button size="sm" disabled={!!busy} onClick={() => download(kind, "pdf")} className="font-semibold"
                    style={{ background: GOLD, color: NAVY }} data-testid={`om-report-${kind}-pdf`}>
                    {busy === `${kind}-pdf` ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                    PDF
                  </Button>
                  <Button size="sm" variant="outline" disabled={!!busy} onClick={() => download(kind, "xlsx")}
                    style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid={`om-report-${kind}-excel`}>
                    {busy === `${kind}-xlsx` ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
                    Excel
                  </Button>
                  {kind === "equipment" ? <Pill tone="gold">asset specific</Pill> : null}
                </div>
              </div>
            </div>
          </OmCard>
        ))}
      </div>
      <p className="text-[11.5px] mt-3" style={{ color: MUTED }} data-testid="om-report-note">
        Reports are generated server-side and scoped to your company only. Excel packs include a chart sheet.
      </p>
    </div>
  );
}
